import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api_middleware";

// Fetch all deployments for an organization
export const fetchDeployments = createAsyncThunk(
  "deployments/fetchDeployments",
  async (organizationId: string) => {
    const response = await api(
      `deployments/all/${organizationId}?allow_testbed_deployments=true`,
      "GET",
      undefined,
      { usePixelsApi: true }
    );
    return response;
  }
);

// Fetch a single deployment by ID
export const fetchDeployment = createAsyncThunk(
  "deployments/fetchDeployment",
  async ({ deploymentId, organizationId }: { deploymentId: string, organizationId: string }) => {
    const response = await api(
      `deployments/${deploymentId}?organization_id=${organizationId}`,
      "GET",
      undefined,
      { usePixelsApi: true }
    );
    return response;
  }
);

// Fetch latest deployment for an organization
export const fetchLatestDeployment = createAsyncThunk(
  "deployments/fetchLatestDeployment",
  async (organizationId: string) => {
    const response = await api(
      `deployments/latest/${organizationId}`,
      "GET",
      undefined,
      { usePixelsApi: true }
    );
    return response;
  }
);

// Fetch latest deployments for all organizations
export const fetchAllLatestDeployments = createAsyncThunk(
  "deployments/fetchAllLatestDeployments",
  async () => {
    const response = await api(
      "deployments/latest",
      "GET",
      undefined,
      { usePixelsApi: true }
    );
    return response as Record<string, any>;
  }
);

// Create a new deployment
export interface CreateDeploymentParams {
  organization_id: string;
  build_id: string;
  min_hosts: number | null;
  max_hosts: number | null;
  behavior_config_id: string;
  is_testbed_deployment?: boolean;
  disable_phantom_sessions?: boolean;
}

export const createDeployment = createAsyncThunk(
  "deployments/createDeployment",
  async (params: CreateDeploymentParams) => {
    const response = await api(
      "deployments/create-initial-deployment",
      "POST",
      params,
      { usePixelsApi: true }
    );
    return response;
  }
);

// Update a deployment
export interface UpdateDeploymentParams {
  organization_id: string;
  original_deployment_id: string;
  build_id?: string;
  min_hosts?: number | null;
  max_hosts?: number | null;
  behavior_config_id?: string;
  is_testbed_deployment?: boolean;
  disable_phantom_sessions?: boolean;
}

export const updateDeployment = createAsyncThunk(
  "deployments/updateDeployment",
  async (params: UpdateDeploymentParams) => {
    const response = await api(
      "deployments/update-deployment",
      "POST",
      params,
      { usePixelsApi: true }
    );
    return response;
  }
);

// Bulk update deployments
export interface BulkUpdateDeploymentsParams {
  build_id: string;
  organization_ids: string[];
}

export interface BulkDeploymentOrganizationResult {
  organization_id: string;
  organization_name: string;
  success: boolean;
  new_deployment_id?: string;
  previous_build_id?: string;
  error_message?: string;
}

export interface BulkUpdateDeploymentsResponse {
  total_requested: number;
  successful: number;
  failed: number;
  results: BulkDeploymentOrganizationResult[];
}

export const bulkUpdateDeployments = createAsyncThunk(
  "deployments/bulkUpdateDeployments",
  async (params: BulkUpdateDeploymentsParams) => {
    const response = await api(
      "deployments/bulk-update",
      "POST",
      params,
      { usePixelsApi: true }
    );
    return response as BulkUpdateDeploymentsResponse;
  }
);
